import { buildApiUrl } from '@/lib/api/base-url';
import {
  clearAccessToken,
  getAccessToken,
  resetMissingTokenLog,
  setAccessToken,
} from '@/lib/auth/token-storage';
import { emitAuthEvent } from '@/lib/auth/auth-events';

const REFRESH_ENDPOINT = '/oauth/session/refresh';
const LOGOUT_ENDPOINT = '/oauth/session/logout';
const LOGIN_ENDPOINT = '/oauth/login';

/**
 * Returns the dev access token if running in development mode and VITE_DEV_ACCESS_TOKEN is set.
 * This allows local development without OAuth by hardcoding a token in .env.local.
 */
function getDevAccessToken(): string | null {
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_ACCESS_TOKEN) {
    return import.meta.env.VITE_DEV_ACCESS_TOKEN as string;
  }
  return null;
}

/**
 * Returns true if dev token authentication is active.
 */
export function isDevTokenAuthActive(): boolean {
  return getDevAccessToken() !== null;
}
const AUTH_REDIRECT_INFO_KEY = 'cellfie:auth-redirect-info';
const AUTH_INFO_TTL_MS = 1000 * 60 * 5; // 5 minutes
const AUTH_MESSAGE_MAX_LENGTH = 180;
const FALLBACK_AUTH_ERROR_MESSAGE = 'session-expired';
const REFRESH_MARGIN_SECONDS = 10;
const MIN_REFRESH_DELAY_SECONDS = 5;

type RefreshResponse = {
  access_token?: string;
  expires_in?: number;
  token_type?: string;
};

type AuthRedirectInfo = {
  status: 'success' | 'error';
  message?: string | null;
  timestamp: number;
};

let refreshTimer: number | null = null;
let pendingRefreshPromise: Promise<string | null> | null = null;
let isInitialized = false;

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function getStorage(): Storage | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function persistAuthRedirectInfo(info: AuthRedirectInfo | null) {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  if (!info) {
    storage.removeItem(AUTH_REDIRECT_INFO_KEY);
    return;
  }

  storage.setItem(AUTH_REDIRECT_INFO_KEY, JSON.stringify(info));
}

export function consumeAuthRedirectInfo(): AuthRedirectInfo | null {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  const raw = storage.getItem(AUTH_REDIRECT_INFO_KEY);

  if (!raw) {
    return null;
  }

  storage.removeItem(AUTH_REDIRECT_INFO_KEY);

  try {
    const info = JSON.parse(raw) as AuthRedirectInfo;
    if (Date.now() - info.timestamp > AUTH_INFO_TTL_MS) {
      return null;
    }
    return info;
  } catch {
    return null;
  }
}

export function peekAuthRedirectInfo(): AuthRedirectInfo | null {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  const raw = storage.getItem(AUTH_REDIRECT_INFO_KEY);

  if (!raw) {
    return null;
  }

  try {
    const info = JSON.parse(raw) as AuthRedirectInfo;
    if (Date.now() - info.timestamp > AUTH_INFO_TTL_MS) {
      return null;
    }
    return info;
  } catch {
    return null;
  }
}

function stripControlCharacters(value: string): string {
  let result = '';
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]!;
    const code = char.charCodeAt(0);
    if ((code >= 0x20 || code === 0x09 || code === 0x0a || code === 0x0d) && code !== 0x7f) {
      result += char;
    }
  }
  return result;
}

function sanitizeAuthMessage(rawMessage: string | null): string | null {
  if (!rawMessage) {
    return null;
  }

  const withoutControlChars = stripControlCharacters(rawMessage);
  const cleaned = withoutControlChars.replace(/<[^>]*>/g, '').trim();

  if (!cleaned) {
    return null;
  }

  if (cleaned.length > AUTH_MESSAGE_MAX_LENGTH) {
    return cleaned.slice(0, AUTH_MESSAGE_MAX_LENGTH);
  }

  return cleaned;
}

function captureAuthRedirectInfoFromUrl(): AuthRedirectInfo | null {
  if (!isBrowser()) {
    return null;
  }

  const url = new URL(window.location.href);
  const authStatus = url.searchParams.get('auth');

  if (!authStatus) {
    return null;
  }

  const normalizedStatus = authStatus === 'error' ? 'error' : 'success';
  const info: AuthRedirectInfo = {
    status: normalizedStatus,
    message: sanitizeAuthMessage(url.searchParams.get('message')),
    timestamp: Date.now(),
  };

  persistAuthRedirectInfo(info);

  url.searchParams.delete('auth');
  url.searchParams.delete('message');
  const nextUrl = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
  window.history.replaceState({}, '', nextUrl);

  return info;
}

function scheduleRefresh(expiresInSeconds?: number) {
  if (!isBrowser()) {
    return;
  }

  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
  }

  if (!expiresInSeconds || expiresInSeconds <= 0) {
    return;
  }

  const refreshDelaySeconds = Math.max(
    expiresInSeconds - REFRESH_MARGIN_SECONDS,
    MIN_REFRESH_DELAY_SECONDS
  );
  const refreshDelayMs = refreshDelaySeconds * 1000;

  refreshTimer = window.setTimeout(() => {
    refreshAccessToken({ silent: true }).catch(() => {
      // Silent failures are handled by consumers when an actual API call happens.
    });
  }, refreshDelayMs);
}

function cancelScheduledRefresh() {
  if (!isBrowser()) {
    refreshTimer = null;
    return;
  }

  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

async function requestNewAccessToken(): Promise<RefreshResponse> {
  const response = await fetch(buildApiUrl(REFRESH_ENDPOINT), {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = new Error(`Failed to refresh access token (${response.status})`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json().catch(() => ({}))) as RefreshResponse;

  return data;
}

function redirectToLoginPage() {
  if (!isBrowser()) {
    return;
  }

  if (window.location.pathname === '/login') {
    return;
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const normalizedRedirectPath = normalizePostLoginRedirectPath(currentPath);
  const loginUrl = new URL('/login', window.location.origin);

  if (normalizedRedirectPath && normalizedRedirectPath !== '/login') {
    loginUrl.searchParams.set('redirect', normalizedRedirectPath);
  }

  window.location.href = `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`;
}

export function handleAuthFailure(options?: {
  reason?: string | null;
  redirectToLogin?: boolean;
  suppressRedirectInfo?: boolean;
}) {
  const hadToken = Boolean(getAccessToken());
  cancelScheduledRefresh();
  clearAccessToken();
  resetMissingTokenLog();
  emitAuthEvent('session-cleared');

  if (options?.suppressRedirectInfo) {
    // When suppressing redirect info (e.g., silent refresh failure),
    // preserve any existing auth redirect info (e.g., from backend AD auth error)
    // Only clear if user had an active token (actual session expiry)
    if (hadToken) {
      persistAuthRedirectInfo(null);
    }
    // If no token existed, leave existing redirect info intact
  } else {
    const message = sanitizeAuthMessage(options?.reason ?? null) ?? FALLBACK_AUTH_ERROR_MESSAGE;
    persistAuthRedirectInfo({
      status: 'error',
      message,
      timestamp: Date.now(),
    });
  }

  if (options?.redirectToLogin) {
    redirectToLoginPage();
  }
}

export async function refreshAccessToken(options?: { silent?: boolean }): Promise<string | null> {
  if (!isBrowser()) {
    return null;
  }

  if (!pendingRefreshPromise) {
    pendingRefreshPromise = (async () => {
      try {
        const payload = await requestNewAccessToken();
        if (!payload.access_token) {
          throw new Error('Access token missing from refresh response');
        }

        setAccessToken(payload.access_token);
        resetMissingTokenLog();
        scheduleRefresh(payload.expires_in);
        return payload.access_token;
      } catch (error) {
        handleAuthFailure({
          reason: FALLBACK_AUTH_ERROR_MESSAGE,
          redirectToLogin: !options?.silent,
          suppressRedirectInfo: options?.silent,
        });
        throw error;
      }
    })();
  }

  const currentPromise = pendingRefreshPromise;

  try {
    return await currentPromise;
  } catch (error) {
    if (options?.silent) {
      return null;
    }
    throw error;
  } finally {
    if (pendingRefreshPromise === currentPromise) {
      pendingRefreshPromise = null;
    }
  }
}

export async function initializeAuthSession(): Promise<void> {
  if (!isBrowser() || isInitialized) {
    return;
  }

  // Check for dev token first (local development only)
  const devToken = getDevAccessToken();
  if (devToken) {
    setAccessToken(devToken);
    resetMissingTokenLog();
    isInitialized = true;
    console.info('[auth] Using dev access token from VITE_DEV_ACCESS_TOKEN');
    return;
  }

  const redirectInfo = captureAuthRedirectInfoFromUrl();

  if (redirectInfo?.status === 'error' && window.location.pathname !== '/login') {
    redirectToLoginPage();
    return;
  }

  // Attempt OAuth token refresh on app load
  // This will either restore the session from the HTTP-only cookie or fail silently
  await refreshAccessToken({ silent: true });

  isInitialized = true;
}

export async function logoutCurrentSession(): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  cancelScheduledRefresh();
  clearAccessToken();
  resetMissingTokenLog();
  emitAuthEvent('session-cleared');

  try {
    await fetch(buildApiUrl(LOGOUT_ENDPOINT), {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('[auth] Failed to notify server about logout', error);
  }
}

export function normalizePostLoginRedirectPath(rawRedirect: string | null | undefined): string | null {
  if (!isBrowser() || !rawRedirect) {
    return null;
  }

  const trimmed = rawRedirect.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const redirectUrl = new URL(trimmed, window.location.origin);
    if (redirectUrl.origin !== window.location.origin) {
      return null;
    }
    return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
  } catch {
    return null;
  }
}

export function redirectToOAuthLogin(redirectPath?: string | null): void {
  if (!isBrowser()) {
    return;
  }

  const loginUrl = new URL(buildApiUrl(LOGIN_ENDPOINT), window.location.origin);
  const normalizedRedirectPath = normalizePostLoginRedirectPath(redirectPath);

  if (normalizedRedirectPath) {
    loginUrl.searchParams.set('redirect', normalizedRedirectPath);
  }

  window.location.href = loginUrl.toString();
}
